/**
 * useDuelNotifications
 *
 * Polle l'endpoint /duels/pending-count toutes les POLL_INTERVAL_MS et compare
 * les compteurs avec le dernier état mémorisé dans localStorage.
 *
 *  - Nouveau défi reçu / à toi de jouer → `event` (petit toast texte).
 *  - Duel(s) qui viennent de se TERMINER → `duelResults` : une FILE d'objets
 *    résultat (score, gagné/perdu, ELO, adversaire, thème) que l'UI affiche en
 *    popup, un par un. On les récupère via /duels/my (qui renvoie déjà tout).
 *
 * Chaque duel terminé est signalé AU PLUS UNE FOIS (ids mémorisés en
 * localStorage). `markDuelSeen(userId, duelId)` permet de marquer un duel comme
 * déjà vu (ex. celui qu'on vient de jouer soi-même → pas de popup en double).
 *
 * L'état est mémorisé par user (clé dérivée du user_id) → multi-comptes OK.
 *
 * Usage :
 *   const { event, dismissEvent, duelResults, dismissDuelResult, refresh } =
 *     useDuelNotifications(user?.id);
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../services/api.js';

const POLL_INTERVAL_MS = 30000; // 30s

function storageKey(userId) {
  return `curiosaurus:duelCounts:${userId || 'anon'}`;
}
function notifiedKeyFor(userId) {
  return `curiosaurus:notifiedDuels:${userId || 'anon'}`;
}

function loadPrev(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function savePrev(userId, counts) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(counts));
  } catch {}
}
function loadNotified(userId) {
  let raw = null;
  try { raw = localStorage.getItem(notifiedKeyFor(userId)); } catch {}
  let set;
  try { set = new Set(raw ? JSON.parse(raw) : []); } catch { set = new Set(); }
  return { set, existed: raw !== null };
}
function saveNotified(userId, set) {
  try { localStorage.setItem(notifiedKeyFor(userId), JSON.stringify([...set].slice(-500))); } catch {}
}

/**
 * Marque un duel comme déjà vu (pas de popup pour lui). Appelé quand l'utilisateur
 * vient de jouer ce duel lui-même et en a déjà vu le résultat.
 */
export function markDuelSeen(userId, duelId) {
  if (!duelId) return;
  const { set } = loadNotified(userId);
  set.add(String(duelId));
  saveNotified(userId, set);
}

/** Transforme un duel de /duels/my en objet résultat pour la popup. */
function toResult(d) {
  return {
    duelId: d.id,
    themeName: d.theme?.name || '?',
    themeIcon: d.theme?.icon || '⚔️',
    myScore: d.my_score,
    theirScore: d.their_score,
    opponentName: d.opponent?.display_name || 'Adversaire',
    opponentAvatar: d.opponent?.avatar || '🧑‍🚀',
    iWon: d.i_won, // true / false / null (égalité)
    eloChange: d.elo_change,
    opponentId: d.opponent_id,
    themeId: d.theme_id,
  };
}

export function useDuelNotifications(userId) {
  const [event, setEvent] = useState(null);
  const [duelResults, setDuelResults] = useState([]);
  const prevCounts = useRef(loadPrev(userId));
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  // Ids déjà dans la file (évite les doublons entre deux checks rapprochés).
  const queuedRef = useRef(new Set());

  const check = useCallback(async () => {
    const uid = userIdRef.current;
    if (!uid) return;
    let counts;
    try {
      counts = await api.getPendingDuels();
    } catch {
      return; // silencieux si réseau down
    }
    const prev = prevCounts.current;

    // Défis reçus / à jouer : delta sur compteur (ces compteurs retombent quand on
    // agit → le delta suffit, pas de re-notif).
    const newChallenges = prev ? (counts.challenges_received || 0) - (prev.challenges_received || 0) : 0;
    const newToPlay = prev ? (counts.to_play || 0) - (prev.to_play || 0) : 0;

    // Duels TERMINÉS : suivi par ID → chaque duel signalé AU PLUS UNE FOIS.
    const { set: notified, existed } = loadNotified(uid);
    const completedIds = counts.just_completed_ids || [];
    let freshCompleted = [];
    if (!existed) {
      // 1er passage : on marque les terminés EXISTANTS comme déjà vus, SANS popup
      // (sinon les duels déjà finis popuperaient au premier login).
      completedIds.forEach((id) => notified.add(id));
    } else {
      freshCompleted = completedIds.filter((id) => !notified.has(id));
      freshCompleted.forEach((id) => notified.add(id));
    }
    saveNotified(uid, notified);

    // Pour les fraîchement terminés : on récupère les détails via /duels/my et on
    // les met en FILE de popups (un par un).
    if (freshCompleted.length > 0) {
      const freshSet = new Set(freshCompleted);
      try {
        const mine = await api.getMyDuels();
        const results = (mine || [])
          .filter((d) => d.status === 'complete' && freshSet.has(d.id) && !queuedRef.current.has(d.id))
          .map((d) => { queuedRef.current.add(d.id); return toResult(d); });
        if (results.length > 0) setDuelResults((q) => [...q, ...results]);
      } catch {
        // si /duels/my échoue, on ne perd rien de grave : le duel reste visible
        // dans l'historique (juste pas de popup automatique).
      }
    }

    // Toast texte : nouveau défi > à toi de jouer. (Le "terminé" passe en popup.)
    if (newChallenges > 0) {
      setEvent({
        type: 'challenge',
        count: newChallenges,
        text: newChallenges === 1
          ? 'Tu as reçu un nouveau défi !'
          : `Tu as reçu ${newChallenges} nouveaux défis !`,
        icon: '⚔️',
      });
    } else if (newToPlay > 0) {
      setEvent({
        type: 'to_play',
        count: newToPlay,
        text: newToPlay === 1
          ? "L'adversaire a joué, à toi !"
          : `${newToPlay} duels prêts à jouer !`,
        icon: '🎯',
      });
    }

    prevCounts.current = counts;
    savePrev(uid, counts);
  }, []);

  useEffect(() => {
    if (!userId) return undefined;
    check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [userId, check]);

  // Re-check quand l'app reprend le focus (retour d'onglet / d'arrière-plan).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [check]);

  const dismissEvent = useCallback(() => setEvent(null), []);
  const dismissDuelResult = useCallback(() => {
    setDuelResults((q) => {
      const [, ...rest] = q;
      return rest;
    });
  }, []);

  return { event, dismissEvent, duelResults, dismissDuelResult, refresh: check };
}
